import { useEffect, useRef } from 'react'
import styles from './ShortcutsPanel.module.css'

interface ShortcutsPanelProps {
    visible: boolean
    onClose: () => void
    anchorRef?: React.RefObject<HTMLElement>
}

interface ShortcutEntry {
    action: string
    shortcut: string
}

interface ShortcutCategory {
    title: string
    entries: ShortcutEntry[]
}

const SHORTCUT_CATEGORIES: ShortcutCategory[] = [
    {
        title: 'General',
        entries: [
            { action: 'Deshacer', shortcut: 'Ctrl+Z' },
            { action: 'Rehacer', shortcut: 'Ctrl+Shift+Z' },
            { action: 'Guardar', shortcut: 'Ctrl+S' },
            { action: 'Seleccionar todo', shortcut: 'Ctrl+A' },
            { action: 'Deseleccionar', shortcut: 'Esc' },
        ],
    },
    {
        title: 'Edición',
        entries: [
            { action: 'Copiar', shortcut: 'Ctrl+C' },
            { action: 'Pegar', shortcut: 'Ctrl+V' },
            { action: 'Cortar', shortcut: 'Ctrl+X' },
            { action: 'Duplicar', shortcut: 'Ctrl+D' },
            { action: 'Eliminar', shortcut: 'Delete' },
        ],
    },
    {
        title: 'Movimiento',
        entries: [
            { action: 'Mover 1px', shortcut: '↑ ↓ ← →' },
            { action: 'Mover 10px', shortcut: 'Shift+↑↓←→' },
        ],
    },
    {
        title: 'Vista',
        entries: [
            { action: 'Zoom', shortcut: 'Ctrl+Scroll' },
            { action: 'Mover lienzo', shortcut: 'Espacio+Arrastrar' },
            { action: 'Ajustar', shortcut: 'Ctrl+0' },
        ],
    },
]

export function ShortcutsPanel({ visible, onClose, anchorRef: _anchorRef }: ShortcutsPanelProps) {
    const panelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!visible) return

        const handleMouseDown = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                onClose()
            }
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleMouseDown)
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('mousedown', handleMouseDown)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [visible, onClose])

    if (!visible) return null

    return (
        <>
            <div className={styles.overlay} onClick={onClose} />
            <div ref={panelRef} className={styles.panel}>
                <h3 className={styles.title}>Atajos de teclado</h3>
                {SHORTCUT_CATEGORIES.map((category) => (
                    <div key={category.title} className={styles.category}>
                        <div className={styles.categoryTitle}>{category.title}</div>
                        {category.entries.map((entry) => (
                            <div key={entry.action} className={styles.row}>
                                <span className={styles.action}>{entry.action}</span>
                                <span className={styles.shortcut}>{entry.shortcut}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </>
    )
}
